import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { useLang } from "../contexts/LangContext";
import { BookOpen, Calendar, ArrowRight, Lock } from "lucide-react";

export default function Magazine() {
  const { t } = useLang();
  const [issues, setIssues] = useState([]);
  useEffect(() => {
    api.get("/magazine/issues").then((r) => setIssues(r.data.issues || [])).catch(() => {});
  }, []);

  return (
    <div data-testid="magazine-page">
      {/* HERO */}
      <section className="relative overflow-hidden bg-[#0a0a0a] text-white py-16 md:py-24">
        <img src="https://images.pexels.com/photos/3651521/pexels-photo-3651521.jpeg" alt="" className="absolute inset-0 w-full h-full object-cover opacity-30 animate-ken-burns" />
        <div className="absolute inset-0 bg-gradient-to-r from-black via-black/35 to-black/15" />
        <div className="relative container-xl">
          <div className="text-xs uppercase tracking-[0.4em] text-[#C9A961] font-semibold mb-3 flex items-center gap-3">
            <BookOpen className="h-3.5 w-3.5" /> Quarterly · 4 numeri all'anno
          </div>
          <h1 className="editorial-heading text-4xl md:text-7xl mb-4">
            Amazonico<br /><span className="italic text-[#C9A961]">Magazine.</span>
          </h1>
          <p className="text-white/80 max-w-2xl text-base md:text-lg">
            {t("section.magazine_sub", "La rivista trimestrale di vino, cucina e cultura italiana.")}
          </p>
        </div>
      </section>

      {/* ISSUES GRID */}
      <section className="container-xl py-12 md:py-16">
        <h2 className="editorial-heading text-3xl md:text-4xl mb-8">I numeri 2026</h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6" data-testid="magazine-grid">
          {issues.map((it) => {
            const coverUrl = it.cover.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${it.cover}` : it.cover;
            return (
            <article key={it.id} className="bg-card border border-border rounded-sm overflow-hidden group" data-testid={`magazine-${it.id}`}>
              <div className="aspect-[3/4] relative overflow-hidden">
                <img src={coverUrl} alt={it.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" loading="lazy" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-black/20 to-transparent" />
                <div className="absolute top-3 left-3 px-3 py-1 bg-[#C9A961] text-black text-[10px] font-bold uppercase tracking-wider rounded-full">{it.season}</div>
                {!it.downloadable && (
                  <div className="absolute top-3 right-3 px-2 py-1 bg-white/15 backdrop-blur text-white text-[9px] uppercase tracking-wider rounded-full flex items-center gap-1">
                    <Lock className="h-3 w-3" /> In arrivo
                  </div>
                )}
                <div className="absolute bottom-0 left-0 p-4 text-white">
                  <div className="editorial-heading text-2xl leading-tight">{it.title}</div>
                  <div className="text-[10px] uppercase tracking-wider text-white/70 mt-1 flex items-center gap-1">
                    <Calendar className="h-3 w-3" /> {new Date(it.published_at).toLocaleDateString("it-IT", { month: "long", year: "numeric" })} · {it.pages} pagine
                  </div>
                </div>
              </div>
              <div className="p-4">
                <p className="text-sm text-muted-foreground mb-3 line-clamp-2">{it.subtitle}</p>
                <ul className="text-xs text-muted-foreground space-y-1 mb-4">
                  {it.highlights.slice(0, 3).map((h) => (<li key={h} className="flex items-start gap-1.5"><span className="text-[#C9A961]">·</span> {h}</li>))}
                </ul>
                {it.downloadable ? (
                  <Link to={`/rivista/${it.id}`} className="inline-flex items-center gap-1.5 text-sm font-semibold text-[hsl(var(--primary))] hover:gap-2 transition-all">
                    Leggi l'estratto <ArrowRight className="h-3.5 w-3.5" />
                  </Link>
                ) : (
                  <span className="inline-flex items-center gap-1.5 text-sm text-muted-foreground">In arrivo a {new Date(it.published_at).toLocaleDateString("it-IT", { month: "long" })}</span>
                )}
              </div>
            </article>
          );})}
        </div>

        {/* SUBSCRIPTION CTA */}
        <div className="mt-14 bg-gradient-to-br from-[#7A1C2B] to-[#5A0F1F] text-white rounded-sm p-8 md:p-12 text-center" data-testid="magazine-cta">
          <BookOpen className="h-10 w-10 text-[#C9A961] mx-auto mb-4" />
          <h2 className="editorial-heading text-3xl mb-2">Abbonati alla rivista</h2>
          <p className="text-white/80 max-w-xl mx-auto mb-6">4 numeri all'anno, in versione cartacea e digitale. Spedizione gratuita in tutta Italia.</p>
          <Link to="/abbonamenti" className="inline-flex items-center gap-2 px-7 py-3 bg-[#C9A961] text-black font-bold uppercase tracking-wider text-xs rounded-sm hover:bg-white transition-colors">
            Scopri gli abbonamenti <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}
